import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { readSingleDevice } from '../helpers'
import Modal from '../../../components/shared/modal'
import ServiceHistoryForm from '../forms/service-history-form'
import { readDeviceServiceHistory } from '../service-history-helpers'
import { getThumbnail } from '../../../components/shared/file-uploader'
import { useAppSelector } from '../../../hooks/redux'

interface ServiceHistoryEntry {
    uid: string
    name: string
    createdAt?: string
    description?: string
    reportType?: string
    images?: string[]
}

import type { DeviceEntity } from './parse-device-data'

/**
 * Device service history page.
 *
 * Loads the device record (if missing) and its associated service
 * history entries from the redux store, and offers a modal form to
 * create new entries.
 */
const DeviceHistory = () => {
    const { t } = useTranslation()
    const { deviceUid } = useParams<{ deviceUid: string }>()

    const device = useAppSelector(state => (
        (state.devices.data as DeviceEntity[]).find(item => item.uid === deviceUid)
    ))
    const deviceServiceHistory = useAppSelector(
        state => state.devices.deviceServiceHistory as ServiceHistoryEntry[] | undefined,
    )

    const [serviceHistoryModal, setServiceHistoryModal] = useState(false)
    const [imageModal, setImageModal] = useState<string | null>(null)

    useEffect(() => {
        if (!device && deviceUid) {
            readSingleDevice(deviceUid)
        }

        if (deviceUid) {
            readDeviceServiceHistory(deviceUid)
        }
    }, [deviceUid, device])

    const toggleServiceHistoryModal = (): void =>
        setServiceHistoryModal(previous => !previous)

    return (
        <div className="container">
            <div className="border-bottom border-info pb-2 mb-4 align-items-center d-flex justify-content-between flex-row flex-wrap">
                <h1 className="mb-0">
                    <Link to={`/devices/${device?.uid || deviceUid}`}>
                        <i className="fas fa-arrow-left mr-2 text-dark" />
                    </Link>
                    {device?.name}
                </h1>
            </div>

            <div className="mt-3 d-flex flex-row flex-wrap">
                <div>
                    <div className="btn btn-info" onClick={toggleServiceHistoryModal}>
                        {t('deviceServiceHistory.list.setNew')}
                    </div>
                </div>
            </div>

            <div className="mt-3 d-flex flex-row flex-wrap">
                {deviceServiceHistory?.map?.(serviceHistory => (
                    <div
                        key={serviceHistory.uid}
                        className={classNames({
                            'card w-100': true,
                            [`service-history-report-${serviceHistory.reportType || 'normal'}`]: true,
                        })}
                    >
                        <div className="card-body">
                            <h5 className="card-title">
                                {serviceHistory.name}
                            </h5>
                            <h6 className="card-subtitle mb-2 text-muted">
                                {window.moment.utc(serviceHistory.createdAt || '').local().format('llll')}
                            </h6>
                            <div
                                className="card-text"
                                dangerouslySetInnerHTML={{ __html: serviceHistory.description ?? '' }}
                            />
                            <div className="d-flex flex-row flex-wrap">
                                {serviceHistory?.images?.map?.((historyImage, index) => (
                                    <div
                                        onClick={() => setImageModal(historyImage)}
                                        className="p-1 cursor-pointer-opacity"
                                        key={index}
                                    >
                                        <img
                                            src={getThumbnail(historyImage)}
                                            alt=""
                                            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                            onError={event => {
                                                (event.target as HTMLImageElement).src = historyImage
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {serviceHistoryModal && (
                <Modal
                    description={t('deviceServiceHistory.list.setNew')}
                    handleHideModal={toggleServiceHistoryModal}
                    dialogClass="modal-lg"
                    content={() => (
                        <div className="p-3">
                            <ServiceHistoryForm
                                device={device}
                                onSuccess={toggleServiceHistoryModal}
                            />
                        </div>
                    )}
                />
            )}

            {imageModal && (
                <Modal
                    description=""
                    handleHideModal={() => setImageModal(null)}
                    dialogClass="modal-lg"
                    content={() => (
                        <div className="p-3">
                            <img src={imageModal} alt="" width="100%" />
                        </div>
                    )}
                />
            )}
        </div>
    )
}

export default DeviceHistory
